import { CloseOutlined, PlusOutlined, SyncOutlined } from '@ant-design/icons';
import { Button, ButtonProps, Status } from '@mm/components';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useSalesForceCRUDActions } from '@mm/state/actions/SalesForceCRUDActions';
import ConnectionModel from '@mm/state/model/ConnectionModel';
import { SalesForceAccount } from '@mm/state/model/SalesForceModel';
import { selectSalesForceConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { colors } from '@mm/utils';
import { notification } from 'antd';
import { ReactComponent as CustomersAiLogo } from 'assets/icons/customersai-logo.svg';
import classNames from 'classnames';
import React, { MouseEvent, ReactNode, useState } from 'react';
import { useSelector } from 'react-redux';
import { ButtonColor } from 'src/common/buttons/PrimaryButton/PrimaryButton';
import { ContactsSync } from '../ContactsSync/ContactsSync';
import { DisabledTagsMessage } from './DisabledTagsMessage';
import { SalesForceConnection } from './SalesForceConnection';
import styles from './SalesForceIntegrations.module.css';
import { useConnectSalesForce } from './use-connect-salesforce';
import { useTagsCommon } from './use-tags-common';

interface Props {
  account: SalesForceAccount;
  oAuthButton: (
    buttonName?: string | ReactNode,
    buttonProps?: Pick<ButtonProps, 'size' | 'color' | 'css' | 'disabled'>,
  ) => ReactNode;
}

export const SalesForceSettings = ({ account, oAuthButton }: Props) => {
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const hasNativeIntegrationsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).nativeIntegrations;
  const sfConnections = useSelector(selectSalesForceConnections);
  const [isAllContactsSync, setIsAllContactsSync] = useState(
    sfConnections.length === 1 && !sfConnections[0].audienceId,
  );
  const accountReadOnly = account.readOnly;

  const accounts = useSelector(
    (state: StoreState) => state.salesForce.accounts,
  );

  const salesForceAccount = accounts?.[0];
  const erroredAccount = salesForceAccount?.status === 'error';

  const errorsArray: { errorCode?: string; message?: string }[] =
    salesForceAccount?.errorInfo && JSON.parse(salesForceAccount?.errorInfo);

  const { update, create, deleteConnection } = useConnectionCRUDActions();
  const { revokeAccess } = useSalesForceCRUDActions();
  const { retrieveSalesForceAccounts } = useConnectSalesForce();
  const { openIntegrationMappingModal } = useModalActions();
  const { tagsEnabled } = useTagsCommon(
    sfConnections[0].salesforceAccessToken?.id,
  );

  const { companyName = 'Customers.ai', isWhitelabelCompany } =
    useWhitelabelConfig();

  const updateWebhook = async (
    params: Partial<ConnectionModel>,
    connection: ConnectionModel,
  ) => {
    if (!connection) return;
    const isDeactivatingConnection = !connection.active;

    if (!isDeactivatingConnection && !hasNativeIntegrationsFeature) {
      return;
    }

    await update(facebookPageId, {
      id: connection.id,
      ...params,
    });
  };

  const revoke = async (e?: MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    if (account) {
      const revoked = await revokeAccess(account.id);
      if (revoked) {
        retrieveSalesForceAccounts();
      }
    }
  };

  const onAddClick = () => {
    if (!hasNativeIntegrationsFeature) return;

    create(facebookPageId, {
      name: 'SalesForce Connection',
      connectionKind: 'salesforce',
      salesforceParams: { accessTokenId: account.id },
    });
  };

  const deleteSFWebhook = async (sfConnection: ConnectionModel) => {
    await deleteConnection(facebookPageId, sfConnection);
    notification.success({ message: 'Connection has been deleted' });
  };

  const showAudiencesBox = !(tagsEnabled === false && isAllContactsSync); // hide if tags disabled and all contacts selected

  return (
    <div className={styles.settingsContent}>
      <div className={classNames(styles.box, styles.boxWithHelp)}>
        <div className={styles.boxContent}>
          <div className={styles.heading}>Connect to SalesForce directly</div>
          <div className={styles.connectionBlock}>
            <Status
              status={erroredAccount ? 'Error' : 'Connected'}
              color={erroredAccount ? colors.red : colors.green5}
              className={styles.status}
              css={{ statusIndicator: styles.statusIndicator }}
            />

            {oAuthButton(
              <>
                <SyncOutlined />
                Reconnect
              </>,
              {
                color: 'secondary-blue',
                css: { container: styles.button },
                disabled: accountReadOnly,
              },
            )}
            {account && (
              <Button
                css={{ container: styles.button }}
                color="secondary-blue"
                onClick={revoke}
                disabled={accountReadOnly}
              >
                <CloseOutlined />
                Disconnect
              </Button>
            )}
            {tagsEnabled === false && <DisabledTagsMessage />}
          </div>
          <div className={styles.error}>
            {errorsArray?.map(item => item?.message || item)}
          </div>
        </div>
      </div>
      <ContactsSync
        isReadOnly={!!accountReadOnly}
        allConnections={sfConnections}
        connection={sfConnections[0]}
        name="SalesForce"
        withSettings
        withSwitch
        onSettingsClick={() =>
          openIntegrationMappingModal({
            connection: sfConnections[0],
          })
        }
        onRadioChange={setIsAllContactsSync}
      >
        {showAudiencesBox && (
          <div
            className={classNames(styles.gridAll, styles.gridSingle, {
              [styles.connectionsContainer]: !isAllContactsSync,
              [styles.gridAll]: isAllContactsSync,
              [styles.gridTwoColumns]: tagsEnabled === false,
            })}
          >
            {!isAllContactsSync && (
              <span
                className={classNames(
                  styles.heading,
                  styles.audienceSelect,
                  styles.gridItem,
                )}
              >
                {!isWhitelabelCompany && (
                  <CustomersAiLogo className={styles.gridIcon} />
                )}
                {companyName} Audience
              </span>
            )}
            {tagsEnabled && (
              <span className={classNames(styles.heading, styles.gridItem)}>
                Add Tag to contacts in SalesForce
              </span>
            )}
            {!isAllContactsSync && (
              <span className={classNames(styles.heading, styles.gridItem)}>
                Actions
              </span>
            )}
            {sfConnections.map(sfConnection => (
              <SalesForceConnection
                key={sfConnection.id}
                connection={sfConnection}
                radioAllContacts={isAllContactsSync}
                facebookPageId={facebookPageId}
                updateWebhook={updateWebhook}
                deleteWebhook={deleteSFWebhook}
              />
            ))}

            <div>
              {!isAllContactsSync && (
                <Button
                  color={ButtonColor.secondaryBlue}
                  onClick={onAddClick}
                  leftIcon={<PlusOutlined />}
                >
                  Add
                </Button>
              )}
              {tagsEnabled === true && (
                <div className={styles.note}>
                  All leads will be tagged “{companyName}”
                </div>
              )}
            </div>
          </div>
        )}
      </ContactsSync>
    </div>
  );
};
